import { ComponentProvider } from '@atoms/ComponentProvider';
import { DialogDiagnosticProvider } from '@atoms/DiagnosticContext';
import { ExpandedExpectation, ExpectationLayoutType } from '@common/DataQuality/dataQualityTypes';
import { useDataQualityDashboard } from '@common/DataQuality/hook';
import { defaultversionAspect } from '@common/DataQuality/reducer';
import { validateExpectationData } from '@common/DataQuality/util';
import { IDEModeProvider } from '@common/ide/IdeMode';
import { QueryKeys, updateEntityVersionedAspectQuery } from '@common/queries/common';
import { validateDatasetExpectationsQuery } from '@common/queries/dataset';
import { EditMode } from '@common/types';
import { Entity } from '@common/types/Entity';
import { UISpecElement } from '@common/types/specTypes';
import { EntityEvents } from '@common/user-analytics';
import { AspectKind } from '@prophecy/graphql-types/enums';
import { theme } from '@prophecy/theme';
import { Stack, Button } from '@prophecy/ui-v3';
import { toast } from '@prophecy/ui-v3/ui-compat';
import { usePrompt } from '@prophecy/utils/react/router';
import { parseJSON } from '@prophecy/utils/string';
import { useQueryClient } from '@tanstack/react-query';
import { isEqual } from 'lodash-es';
import React, { useEffect, useMemo, useState } from 'react';
import { Provider } from 'react-redux';
import styled from 'styled-components';

import { useMixPanel } from '../../../context/mixpanel/context';
import { useGraphQlMutation } from '../../../data/util';
import { DatasetListType } from '../../../generic-components/SchemaTable/datasetTypes';
import { Parser } from '../../../Parser';

const saveConfirmationText = 'Are you sure, you want to leave without saving the expectations?';
const StyledForm = styled.form`
  width: 100%;
  height: 100%;
`;

export const Expectation = ({
  uid,
  branch,
  datasets,
  setExpectationChange
}: {
  uid: string;
  branch: string;
  datasets: DatasetListType[];
  setExpectationChange: (isChange: boolean) => void;
}) => {
  const { store, dqData, hasData } = useDataQualityDashboard({ branch, datasets, datasetId: uid, isDatasetInfo: true });

  const dqSpec = {
    id: 'sds',
    kind: 'Atoms.DataQuality',
    properties: {
      data: '${$.expectation.metainfo.versionedAspect}',
      height: '100% - 60px'
    },
    contains: []
  } as UISpecElement;

  const [data, setData] = useState<string>('');
  const [expectationSpec, setSpec] = useState<ExpectationLayoutType | undefined>(undefined);
  store.subscribe(() => {
    const currentState = store.getState();
    const metainfo = currentState.currentGraph?.metainfo;
    const data = metainfo?.versionedAspect || defaultversionAspect;
    setSpec(currentState?.expectation?.expectationSpec);
    setData(data);
  });

  const { track } = useMixPanel();

  const currentData = useMemo(
    () => (data && parseJSON<{ expectations: ExpandedExpectation[] }>(data, { expectations: [] })?.expectations) || [],
    [data]
  );

  const isSameData = currentData && hasData && dqData ? isEqual(parseJSON(data, {}), parseJSON(dqData, {})) : true;

  usePrompt(() => {
    // no need to show error when same data
    if (isSameData) {
      return;
    }
    return saveConfirmationText;
  }, !isSameData);

  useEffect(() => {
    setExpectationChange(isSameData);
    return () => {
      setExpectationChange(isSameData);
    };
  }, [isSameData, setExpectationChange]);

  const queryClient = useQueryClient();
  const versionedAspectMutation = useGraphQlMutation(updateEntityVersionedAspectQuery);

  const { isLoading, mutateAsync } = useGraphQlMutation(validateDatasetExpectationsQuery);
  const onSave = async () => {
    track(EntityEvents[Entity.Dataset].metadata.expectationSave, { uid, branch });
    const errorArray = validateExpectationData(currentData?.length ? currentData : [], expectationSpec);
    if (errorArray.length) {
      toast.error({
        content: (
          <Stack>
            {errorArray?.map((error, index) => (
              <span key={index}>{error}</span>
            ))}
          </Stack>
        )
      });
      return;
    }
    await mutateAsync(
      { uid, entityKind: Entity.Dataset, aspectKind: AspectKind.DataQualityExpectation, aspectValue: data },
      {
        onSuccess: async (resp) => {
          const message = resp?.validateExpectations.message;
          if (message) {
            toast.error({ content: message });
          } else {
            await versionedAspectMutation.mutateAsync(
              {
                uid,
                entityKind: Entity.Dataset,
                aspectKind: AspectKind.DataQualityExpectation,
                aspectValue: data
              },
              {
                onSuccess: () => {
                  queryClient.invalidateQueries([QueryKeys.Dataset]);
                  toast.success({ content: 'Expectations updated successfully.' });
                }
              }
            );
          }
        }
      }
    );
  };

  // Need to set DialogDiagnostics and IDEmode provider because components are consumed inside expectation layout to override the default value
  return (
    <DialogDiagnosticProvider propertyPath={[``]} isMetadata={true}>
      <IDEModeProvider value={EditMode.enabled}>
        <Provider store={store}>
          <ComponentProvider>
            <StyledForm>
              <Stack width='100%' height='100%' align='space-between' alignY='end'>
                <Parser specs={dqSpec} />
                <Stack gap={theme.spaces.x12} align='end' width='100%' direction='horizontal' alignY='center'>
                  <Button
                    loading={versionedAspectMutation.isLoading || isLoading}
                    onClick={onSave}
                    data-test-id='dataset-quality-save-button'>
                    Save
                  </Button>
                </Stack>
              </Stack>
            </StyledForm>
          </ComponentProvider>
        </Provider>
      </IDEModeProvider>
    </DialogDiagnosticProvider>
  );
};
